import React from 'react';
import { Link as RouterLink, LinkProps } from 'react-router-dom';
import cx from 'classnames';
import * as AccessibleIcon from '@radix-ui/react-accessible-icon';
import { IconOptions, IconType } from '@mutiny-pkg/dumpster-ui/utils/sharedTypes';
import * as style from './Link.css';
import * as buttonStyle from '../Button/Button.css';

type AsButtonPropsType = {
  asButton?: boolean;
  buttonVariant?: NonNullable<buttonStyle.ButtonVariants>['variant'];
  buttonSize?: NonNullable<buttonStyle.ButtonVariants>['size'];
  fullWidth?: NonNullable<buttonStyle.ButtonVariants>['fullWidth'];
};

export type LinkVariantTypes = NonNullable<style.LinkStyleVariants>;
interface ITextLinkProps extends Omit<LinkProps, 'to'>, IconOptions {
  href: string;
  children: React.ReactNode;
  linkRef?: React.Ref<HTMLAnchorElement>;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

type TextLinkProps = style.LinkStyleVariants & ITextLinkProps & AsButtonPropsType;

interface IButtonLinkProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>, IconOptions {
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLSpanElement>;
  children: React.ReactNode;
  linkRef?: React.Ref<HTMLButtonElement | HTMLSpanElement>;
  as?: 'button' | 'span';
}

type ButtonLinkProps = style.LinkStyleVariants & IButtonLinkProps & AsButtonPropsType;

export type LinkComponentProps = TextLinkProps | ButtonLinkProps;

export type IconLinkProps = Omit<ITextLinkProps, 'children' | 'iconLeft' | 'iconRight'> &
  style.LinkStyleVariants &
  AsButtonPropsType & {
    icon: IconType;
    iconSize?: style.IconSizeVariant;
    label: string;
    children?: never;
  };

const linkEventHandlers = {
  onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
};

const hasHref = (props: LinkComponentProps): props is TextLinkProps => 'href' in props;

const LinkContent = ({ iconLeft, iconRight, children }: Partial<TextLinkProps | ButtonLinkProps>) => {
  const IconLeft = iconLeft;
  const IconRight = iconRight;

  return (
    <>
      {IconLeft && <IconLeft />}
      {children}
      {IconRight && <IconRight />}
    </>
  );
};

const TextLink = ({
  href,
  linkRef,
  disabled,
  size,
  variant,
  children,
  className,
  asButton,
  buttonVariant,
  iconSize,
  iconOnly,
  buttonSize,
  fullWidth,
  noPaddingX,
  iconLeft,
  iconRight,
  ...props
}: TextLinkProps) => {
  const linkClasses = style.linkStyles({ size, variant, disabled, iconOnly, iconSize, noPaddingX });
  const buttonClasses = cx([
    buttonStyle.button({
      variant: buttonVariant,
      size: buttonSize,
      display: 'inline',
      fullWidth,
      iconOnly,
      iconSize,
    }),
    style.asButtonLink,
  ]);
  const resolvedClasses = cx((asButton && buttonClasses) || linkClasses, className);

  if (disabled) {
    return (
      <button type="button" {...linkEventHandlers} className={resolvedClasses} disabled style={props.style}>
        <LinkContent iconLeft={iconLeft} iconRight={iconRight} {...props}>
          {children}
        </LinkContent>
      </button>
    );
  }

  if (href.match(/^(https?|mailto|data)/)) {
    return (
      <a
        ref={linkRef}
        href={href}
        target="_blank"
        rel="noreferrer"
        {...linkEventHandlers}
        className={resolvedClasses}
        {...props}
      >
        <LinkContent iconLeft={iconLeft} iconRight={iconRight} {...props}>
          {children}
        </LinkContent>
      </a>
    );
  }

  return (
    <RouterLink to={href} {...linkEventHandlers} className={resolvedClasses} {...props}>
      <LinkContent iconLeft={iconLeft} iconRight={iconRight} {...props}>
        {children}
      </LinkContent>
    </RouterLink>
  );
};

const ButtonLink = ({
  linkRef,
  size,
  variant,
  children,
  asButton,
  as: Element = 'button',
  className,
  ...props
}: ButtonLinkProps) => {
  const linkClasses = style.linkStyles({ size, variant });
  const buttonClasses = buttonStyle.button({
    variant: props.buttonVariant,
    size: props.buttonSize,
    display: 'inline',
    fullWidth: props.fullWidth,
  });
  const resolvedClasses = cx((asButton && buttonClasses) || linkClasses, className);

  if (Element === 'span') {
    return (
      <span
        ref={linkRef as React.Ref<HTMLSpanElement>}
        role="button"
        tabIndex={0}
        {...linkEventHandlers}
        onClick={props.onClick as React.MouseEventHandler<HTMLSpanElement>}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            props.onClick?.(e as unknown as React.MouseEvent<HTMLSpanElement>);
          }
        }}
        className={resolvedClasses}
        style={props.style}
      >
        <LinkContent {...props}>{children}</LinkContent>
      </span>
    );
  }

  return (
    <button
      ref={linkRef as React.Ref<HTMLButtonElement>}
      type="button"
      {...linkEventHandlers}
      {...props}
      className={resolvedClasses}
    >
      <LinkContent {...props}>{children}</LinkContent>
    </button>
  );
};

export const Link = (props: LinkComponentProps) => {
  if (hasHref(props)) {
    return <TextLink {...props} />;
  }
  return <ButtonLink {...props} />;
};

export const IconLink = React.forwardRef<HTMLAnchorElement, IconLinkProps>(
  (
    {
      icon: Icon,
      label,
      href,
      iconSize = 'default',
      size,
      variant,
      disabled,
      asButton = true,
      buttonVariant,
      buttonSize,
      fullWidth,
      ...props
    }: IconLinkProps,
    forwardedRef,
  ): JSX.Element => {
    return (
      <TextLink
        href={href}
        linkRef={forwardedRef}
        size={size}
        variant={variant}
        disabled={disabled}
        iconOnly
        iconSize={iconSize}
        asButton={asButton}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        fullWidth={fullWidth}
        {...props}
      >
        <AccessibleIcon.Root label={label}>
          <Icon className={style.iconSizes[iconSize]} />
        </AccessibleIcon.Root>
      </TextLink>
    );
  },
);
